import * as t from './types'
import {
  Organization,
  foldEitherToAction,
  memoed,
  Loading,
  loadable,
} from '@library/react-toolkit'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import { pipe } from 'fp-ts/lib/function'
import { CancelToken } from 'axios'
import {
  takeLatest,
  matchPathGuard,
  waitForNymID,
  waitForOrganization,
} from '../../saga'
import { task, taskEither, option, array } from 'fp-ts'
import { LocationChangeAction } from 'connected-react-router'
import { State } from '../../store'
import { lens } from 'lens.ts'
import { getTransactions } from '../Wallet/api'

const getCurrentPeriod = () => {
  const d = new Date()
  const m = d.getMonth()
  return `${d.getFullYear()}-${m < 10 ? `0${m}` : m}`
}

export const initialState: t.State = {
  query: { page: 0, itemsPerPage: 10 },
  lastTransferDate: memoed.init(option.none),
  currentPeriodSummary: memoed.init({ amount: {}, period: getCurrentPeriod() }),
  periodInfo: memoed.init({ data: [], count: 0 }),
}

const _l = lens<t.State>()

export const reducer = (s: t.State = initialState, a: t.Actions): t.State => {
  switch (a.type) {
    case 'HOME/get_period_info':
      return pipe(
        s,
        _l.query.set(q => ({ ...q, ...a.payload })),
        _l.periodInfo.set(memoed.step<t.PeriodInfoData>(Loading))
      )
    case 'HOME/get_period_info_result':
      return pipe(s, _l.periodInfo.set(memoed.step(a.payload)))
    case 'HOME/get_last_transfer_date':
      return pipe(
        s,
        _l.lastTransferDate.set(memoed.step<option.Option<Date>>(Loading))
      )
    case 'HOME/get_last_transfer_date_result':
      return pipe(s, _l.lastTransferDate.set(memoed.step(a.payload)))
    case 'HOME/get_current_period':
      return pipe(
        s,
        _l.currentPeriodSummary.set(memoed.step<t.PeriodSummary>(Loading))
      )
    case 'HOME/get_current_period_result':
      return pipe(s, _l.currentPeriodSummary.set(memoed.step(a.payload)))
    default:
      return s
  }
}

function* getBillsSaga(_: t.GetPeriodInfoAction, token?: CancelToken) {
  const q: t.State['query'] = yield effects.select((s: State) => s.home.query)

  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.getPeriodInformation(organization.name, q, token),
    task.map(foldEitherToAction(t.getPeriodInfoActionResult)),
    task.map(a => effects.put(a)),
    e => effects.call(e)
  )
}

const getDateFromTransactions = (ts: { data: { createdAt: Date }[] }) =>
  pipe(
    ts.data,
    array.head,
    option.map(tx => tx.createdAt)
  )

function* getLastTransferDateSaga(
  _: t.GetLastTransferDateAction,
  token?: CancelToken
) {
  const {
    query: { page },
    transactions,
  }: State['wallet'] = yield effects.select((s: State) => s.wallet)

  if (page === 0 && loadable.isOk(transactions.v)) {
    yield pipe(
      transactions.v.value,
      getDateFromTransactions,
      t.getLastTransferDateResult.ok,
      a => effects.put(a)
    )
  }

  const userNymID: string = yield waitForNymID()
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    getTransactions(
      organization.name,
      { page: 0, itemsPerPage: 1 },
      userNymID,
      token
    ),
    taskEither.map(getDateFromTransactions),
    task.map(foldEitherToAction(t.getLastTransferDateResult)),
    task.map(a => effects.put(a)),
    e => effects.call(e)
  )
}

function* getCurrentPeriodSaga(_: t.GetPeriodInfoAction, token?: CancelToken) {
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.getPeriodSummary(organization.name, token),
    task.map(foldEitherToAction(t.getCurrentPeriodResult)),
    task.map(a => effects.put(a)),
    e => effects.call(e)
  )
}

function* changePageSaga(_: LocationChangeAction) {
  yield waitForOrganization()

  yield effects.all([
    effects.put(t.getPeriodInfoAction({})),
    effects.put(t.getLastTransferDateAction()),
    effects.put(t.getCurrentPeriodAction()),
  ])
}

export function* saga() {
  yield effects.all([
    takeLatest('HOME/get_period_info', getBillsSaga),
    takeLatest('HOME/get_last_transfer_date', getLastTransferDateSaga),
    takeLatest('HOME/get_current_period', getCurrentPeriodSaga),
    takeLatest(matchPathGuard('/'), changePageSaga),
  ])
}
